import { db } from '../../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { AuditService } from './AuditService';

export type NoteTemplate = 'SOAP' | 'DAP' | 'BIRP' | 'Progress' | 'Initial' | 'Custom';
export type NoteStatus = 'draft' | 'final' | 'signed';
export type ChangeType = 'ai-generated' | 'manual-edit' | 'template-insert' | 'regenerated';

export interface NoteEdit {
  timestamp: string;
  editorId: string;
  changeType: ChangeType;
  previousContent?: string;
  description?: string;
}

export interface ClinicalNote {
  id: string;
  sessionId: string;
  patientId: string;
  providerId: string;
  template: NoteTemplate;
  content: string;
  aiGeneratedContent?: string;
  wasAiAssisted: boolean;
  providerEdited: boolean;
  version: number;
  editHistory: NoteEdit[];
  createdAt: Date;
  lastEditedAt: Date;
  signedBy?: string;
  signedAt?: Date;
  status: NoteStatus;
  customInstructions?: string;
}

export class FirebaseClinicalNotesService {
  private static COLLECTION = 'clinical_notes';

  /**
   * Get all notes for a provider, ordered by most recent.
   */
  static async getNotesByProvider(providerId: string): Promise<ClinicalNote[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.fromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching clinical notes:', error);
      throw error;
    }
  }

  /**
   * Get all notes for a specific patient by the current provider.
   */
  static async getNotesByPatient(providerId: string, patientId: string): Promise<ClinicalNote[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('patientId', '==', patientId),
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.fromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching patient notes:', error);
      throw error;
    }
  }

  /**
   * Get a single note by ID.
   */
  static async getNote(noteId: string): Promise<ClinicalNote | null> {
    try {
      const docSnap = await getDoc(doc(db, this.COLLECTION, noteId));
      if (docSnap.exists()) {
        return this.fromFirestore(docSnap.id, docSnap.data());
      }
      return null;
    } catch (error) {
      console.error('Error fetching note:', error);
      return null;
    }
  }

  /**
   * Create a new clinical note.
   */
  static async createNote(
    noteData: Omit<ClinicalNote, 'id' | 'createdAt' | 'lastEditedAt'>
  ): Promise<string> {
    try {
      const docRef = doc(collection(db, this.COLLECTION));
      await setDoc(docRef, {
        ...noteData,
        editHistory: noteData.editHistory || [],
        createdAt: Timestamp.now(),
        lastEditedAt: Timestamp.now(),
      });
      AuditService.logPhiModification(
        noteData.providerId, 'provider', 'create', 'note', docRef.id, noteData.patientId, 'Created clinical note'
      );
      return docRef.id;
    } catch (error) {
      console.error('Error creating clinical note:', error);
      throw error;
    }
  }

  /**
   * Update an existing note (append to edit history).
   */
  static async updateNote(
    noteId: string,
    updates: Partial<ClinicalNote>,
    editorId: string,
    changeType: ChangeType = 'manual-edit'
  ): Promise<void> {
    try {
      const existing = await this.getNote(noteId);
      if (!existing) throw new Error('Note not found');

      const edit: NoteEdit = {
        timestamp: new Date().toISOString(),
        editorId,
        changeType,
        previousContent: existing.content,
        description: changeType === 'manual-edit' ? 'Provider edit' : changeType,
      };

      const { id, createdAt, editHistory, ...updateData } = updates as any;

      await updateDoc(doc(db, this.COLLECTION, noteId), {
        ...updateData,
        editHistory: [...existing.editHistory, edit],
        version: existing.version + 1,
        providerEdited: true,
        lastEditedAt: Timestamp.now(),
      });
      AuditService.logPhiModification(
        editorId, 'provider', 'update', 'note', noteId, existing.patientId, `Note edited (${changeType}), version ${existing.version + 1}`
      );
    } catch (error) {
      console.error('Error updating clinical note:', error);
      throw error;
    }
  }

  /**
   * Sign a finalized note.
   */
  static async signNote(noteId: string, signedBy: string): Promise<void> {
    try {
      const existing = await this.getNote(noteId);
      await updateDoc(doc(db, this.COLLECTION, noteId), {
        status: 'signed',
        signedBy,
        signedAt: Timestamp.now(),
        lastEditedAt: Timestamp.now(),
      });
      AuditService.logPhiModification(
        signedBy, 'provider', 'update', 'note', noteId, existing?.patientId || '', 'Clinical note signed'
      );
    } catch (error) {
      console.error('Error signing clinical note:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time note updates for a patient (across all providers).
   * providerId param kept for backward compatibility but is not used in the query.
   */
  static subscribeToPatientNotes(
    providerId: string,
    patientId: string,
    callback: (notes: ClinicalNote[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('patientId', '==', patientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.fromFirestore(d.id, d.data())));
    });
  }

  private static fromFirestore(id: string, data: any): ClinicalNote {
    return {
      id,
      sessionId: data.sessionId,
      patientId: data.patientId,
      providerId: data.providerId,
      template: data.template,
      content: data.content,
      aiGeneratedContent: data.aiGeneratedContent,
      wasAiAssisted: data.wasAiAssisted ?? false,
      providerEdited: data.providerEdited ?? false,
      version: data.version ?? 1,
      editHistory: data.editHistory || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      lastEditedAt: data.lastEditedAt?.toDate() || new Date(),
      signedBy: data.signedBy,
      signedAt: data.signedAt?.toDate(),
      status: data.status || 'draft',
      customInstructions: data.customInstructions,
    };
  }
}
